import React, { useContext, useEffect, useState } from 'react';
import { FieldOption, MultiSearchSelectFieldType, FieldData } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import FormFieldContext from '../FormFieldContext';
import { useGlobalContext } from 'GlobalContext';
import MultiSearchSelect from '../basefields/MultiSearchSelect';
import FieldWrapper from '../FieldWrapper';
import '../../../style/scss/forms.scss';

const MultiSearchSelectField: React.FC<MultiSearchSelectFieldType & { data: FieldData, viewKey: string }> = ({
    viewKey,
    query,
    placeholder,
    objectName,
    name,
    data,
    directOptionsTitle,
    selectionFormat = 'name',
    optionFormat,
    label,
    helperText,
    tooltipText,
    viewInEditMode,
    disableAddNew = false,
    disabled,
    alignment,
    isEditable,
    addNewItem,
    showOptionsOnClick,
    specialOptions
}) => {
    const { t } = useTranslation();
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const { setUnsavedChanges, setPreventClosing } = useGlobalContext();
    const [selectedOptions, setSelectedOptions] = useState<FieldOption[]>([]);

    // Merge the current data with the edits so queries can use both
    const queryData = { ...(data || {}), ...updatedData };

    // Set the current selection from the received data
    useEffect(() => {

        // Find a special option which is marked as selected in the data
        const activeSpecialOption = (specialOptions || []).find(special =>
            special.dataField && data?.[special.dataField] === true
        );

        // A selected special option replaces the concrete selection
        if (activeSpecialOption) {
            setSelectedOptions([{ value: activeSpecialOption.value, name: t(activeSpecialOption.label) }]);
            return;
        }

        // Convert the concrete items of the data into selected options
        if (Array.isArray(data?.[name])) {
            setSelectedOptions(data[name].map((item: any) => ({
                ...item,
                id: item.id,
                value: item.id,
                name: item[selectionFormat] ?? item.name
            })));
        }
    }, [data, name]);

    // Store the changed selection in the updated data
    const storeSelection = (options: FieldOption[]) => {
        setSelectedOptions(options);

        // Submit the special option value or the ids of the concrete items
        setUpdatedData({ ...updatedData, [name]: options.map(option => option.id ?? option.value) });

        // Set unsaved changes
        setUnsavedChanges(viewKey, true);
    }

    // Handle adding an option to the selection
    const handleAdd = (option: FieldOption) => {

        // Determine whether the added option is a special option
        const isSpecialOption = (specialOptions || []).some(special => special.value === option.value);

        // A special option excludes every concrete selection and the other way around
        if (isSpecialOption) {
            storeSelection([option]);
        } else {
            const concreteOptions = selectedOptions.filter(selected =>
                !(specialOptions || []).some(special => special.value === selected.value)
            );
            storeSelection([...concreteOptions, option]);
        }
    }

    // Handle adding several options to the selection at once
    const handleAddMany = (options: FieldOption[]) => {

        // Drop a selected special option, the concrete items replace it
        const concreteOptions = selectedOptions.filter(selected =>
            !(specialOptions || []).some(special => special.value === selected.value)
        );

        // Skip the options which are already selected
        const newOptions = options.filter(option =>
            !concreteOptions.some(selected => selected.id === option.id)
        );
        storeSelection([...concreteOptions, ...newOptions]);
    }

    // Handle removing an option from the selection
    const handleRemove = (option: FieldOption) => {
        storeSelection(selectedOptions.filter(selected =>
            !((option.id !== undefined && selected.id === option.id) ||
              (option.id === undefined && selected.value === option.value))
        ));
    }

    // Handle setting the prevent closing state, or enabling it again
    const handleSetPreventClosing = (shouldPreventClose: boolean) => {
        setPreventClosing(viewKey, shouldPreventClose)
    }

    // Format the text of a selected option for the view mode
    const formatViewValue = (option: FieldOption) => {

        // Show the translated label of a selected special option
        const specialOption = (specialOptions || []).find(special => special.value === option.value);
        if (specialOption) return t(specialOption.label);

        return String(option[selectionFormat] ?? option.name ?? '');
    }

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) ? (
                // Edit mode
                <MultiSearchSelect
                    type='multi-searchselect'
                    name={name}
                    data={data}
                    placeholder={placeholder}
                    updatedData={queryData}
                    query={query}
                    objectName={objectName}
                    disabled={disabled}
                    disableAddNew={disableAddNew}
                    showOptionsOnClick={showOptionsOnClick}
                    directOptionsTitle={directOptionsTitle}
                    selectionFormat={selectionFormat}
                    optionFormat={optionFormat}
                    specialOptions={specialOptions}
                    selectedOptions={selectedOptions}
                    onAdd={handleAdd}
                    onAddMany={handleAddMany}
                    onRemove={handleRemove}
                    addNewItem={addNewItem}
                    onPreventClose={(shouldPreventClose) => handleSetPreventClosing(shouldPreventClose)}
                />
            ) : (
                // View mode
                <div className="view-mode">
                    {selectedOptions.length > 0 ? (
                        selectedOptions.map((option, index) => (
                            <span key={String(option.id ?? option.value ?? index)} className='p'>
                                {formatViewValue(option)}{index < selectedOptions.length - 1 ? ', ' : ''}
                            </span>
                        ))
                    ) : (
                        <span className="no-value">
                            <span className='minus-sign'>-</span>
                            <span className="hover-text">{t('forms.add_value')}</span>
                        </span>
                    )}
                </div>
            )}
        </FieldWrapper>
    );
};

export default MultiSearchSelectField;
